import cx from 'classnames';
import {map} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';
import {StepWizardChildProps} from 'react-step-wizard';

import {Icon} from 'components';

type Props = {
    navItems: string[];
} & Partial<StepWizardChildProps>

export const WizardNav: React.FC<Props> = ({navItems, totalSteps, currentStep}) => {
    const currentStepIndex = useMemo(() => currentStep - 1, [currentStep]);
    const lastStepIndex = useMemo(() => totalSteps - 1, [totalSteps]);

    const isPassedStep = useCallback((index: number) =>
        index < currentStepIndex || currentStepIndex === lastStepIndex, [currentStepIndex, lastStepIndex]);

    const renderDivider = useCallback((index: number) => {
        const isPassed = isPassedStep(index);
        return (
            <Fragment>
                <Icon
                    thickness='xl'
                    name='minus'
                    color={isPassed ? 'success' : 'default'}
                    className='tw-ml-4 -lg:tw-hidden'
                />
                <div className={cx('tw-w-2 tw-h-2 tw-rounded-full tw-ml-4 lg:tw-hidden', {
                    'tw-bg-dark-dimmed': !isPassed,
                    'tw-bg-success': isPassed
                })}
                />
            </Fragment>
        );
    }, [isPassedStep]);

    const renderItemNumber = useCallback((index: number) => {
        const isPassed = isPassedStep(index);
        return (
            <div className={cx(
                'tw-text-alternative tw-flex-center tw-w-10 tw-h-10 tw-rounded-full tw-shrink-0', {
                    'tw-bg-success': isPassed,
                    'tw-bg-primary': currentStepIndex === index && !isPassed,
                    'tw-bg-dark-dimmed': currentStepIndex < index
                })}
            >
                {isPassed ?
                    <Icon name='check' color='alternative' /> :
                    <span className='tw-font-bold'>{index + 1}</span>}
            </div>
        );
    }, [currentStepIndex, isPassedStep]);

    const renderItemLabel = useCallback((index: number, label: string) => {
        const isPassed = isPassedStep(index);
        return (
            <div className={cx('-lg:tw-hidden tw-font-bold tw-ml-2', {
                'tw-text-success': isPassed,
                'tw-text-primary': currentStepIndex === index && !isPassed
            })}
            >
                {label}
            </div>
        );
    }, [currentStepIndex, isPassedStep]);

    const renderNavItem = useCallback((label: string, index: number) => (
        <div key={index} className='tw-flex tw-items-center tw-mx-2 tw-text-lg'>
            {renderItemNumber(index)}
            {renderItemLabel(index, label)}
            {index < lastStepIndex && renderDivider(index)}
        </div>
    ), [lastStepIndex, renderDivider, renderItemNumber, renderItemLabel]);

    return navItems?.length === totalSteps ? (
        <div className='tw-mb-12'>
            <div className='tw-flex-center'>
                {map(navItems, renderNavItem)}
            </div>
            <div className='lg:tw-hidden tw-text-center tw-text-primary tw-ts-h5-bold tw-mt-8'>{navItems[currentStepIndex]}</div>
        </div>
    ) : null;
};